import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Textarea, Card, Tooltip, Modal } from '../../components';
import * as pitchService from '../../services/pitch-service';

interface SubmissionData {
  id: string;
  type: 'idea' | 'pitch';
  startupName: string;
  title: string;
  status: string;
  submittedAt: string;
  problemStatement?: string;
  proposedSolution?: string;
  targetMarket?: string;
  uniqueValueProposition?: string;
  problem?: string;
  solution?: string;
  marketSize?: string;
  businessModel?: string;
  traction?: string;
  team?: string;
  financials?: string;
  askAmount?: string;
  useOfFunds?: string;
  competitiveAdvantage?: string;
  fileUrl?: string;
}

const ReadOnlyField: React.FC<{ label: string; value?: string }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <label className="block text-sm font-medium text-muted mb-1">{label}</label>
      <div className="bg-shell rounded-lg p-3 text-text text-sm leading-relaxed whitespace-pre-wrap">{value}</div>
    </div>
  );
};

type Outcome = {
  label: string;
  status: 'rejected' | 'revision_needed' | 'approved';
  distinction: boolean;
  tone: 'danger' | 'warning' | 'success' | 'chart';
  requiresFeedback: boolean;
  message: string;
};

const EMPTY_OUTCOME: Outcome = {
  label: 'Enter a score',
  status: 'approved',
  distinction: false,
  tone: 'chart',
  requiresFeedback: false,
  message: 'Enter a score from 0 to 100 to see the automatic outcome.',
};

const deriveOutcome = (raw: string): Outcome | null => {
  if (raw === '') return null;
  const n = Number(raw);
  if (Number.isNaN(n) || n < 0 || n > 100) return null;
  if (n < 40) {
    return {
      label: 'Rejected',
      status: 'rejected',
      distinction: false,
      tone: 'danger',
      requiresFeedback: true,
      message: 'Submission will be rejected. Feedback is required.',
    };
  }
  if (n < 60) {
    return {
      label: 'Needs Revision',
      status: 'revision_needed',
      distinction: false,
      tone: 'warning',
      requiresFeedback: true,
      message: 'Founder will be asked to revise and resubmit. Feedback on what to revise is required.',
    };
  }
  if (n < 80) {
    return {
      label: 'Approved',
      status: 'approved',
      distinction: false,
      tone: 'success',
      requiresFeedback: false,
      message: 'Submission will be approved.',
    };
  }
  return {
    label: 'Approved with Distinction',
    status: 'approved',
    distinction: true,
    tone: 'chart',
    requiresFeedback: false,
    message: 'Submission will be approved with distinction — exceptional work.',
  };
};

const toneClasses: Record<Outcome['tone'], { pill: string; bar: string }> = {
  danger:  { pill: 'bg-danger/15 text-danger border-danger/30',    bar: 'bg-danger' },
  warning: { pill: 'bg-warning/15 text-warning border-warning/30', bar: 'bg-warning' },
  success: { pill: 'bg-success/15 text-success border-success/30', bar: 'bg-success' },
  chart:   { pill: 'bg-chart/15 text-chart border-chart/30',       bar: 'bg-chart' },
};

const PitchReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState('');
  const [marks, setMarks] = useState<string>(''); // keep as string so '' is distinct from 0
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [loading, setLoading] = useState(true);

  const submissionType = searchParams.get('type') || 'idea';

  useEffect(() => {
    const load = async () => {
      try {
        if (submissionType === 'idea') {
          const idea = await pitchService.getIdeaById(id!);
          setSubmission({
            id: idea._id, type: 'idea',
            startupName: (idea as any).startupId?.name || 'Unknown', title: idea.title,
            status: idea.status, submittedAt: idea.submittedAt || '',
            problemStatement: idea.problemStatement, proposedSolution: idea.proposedSolution,
            targetMarket: idea.targetMarket, uniqueValueProposition: idea.uniqueValueProp,
          });
        } else {
          const pitch = await pitchService.getPitchById(id!);
          setSubmission({
            id: pitch._id, type: 'pitch',
            startupName: (pitch as any).startupId?.name || 'Unknown',
            title: pitch.problem?.slice(0, 60) || 'Pitch Submission',
            status: pitch.status, submittedAt: (pitch as any).submittedAt || '',
            problem: pitch.problem, solution: pitch.solution,
            marketSize: pitch.marketSize, businessModel: pitch.businessModel,
            traction: pitch.traction, team: pitch.team,
            financials: pitch.financials, askAmount: pitch.askAmount,
            useOfFunds: pitch.useOfFunds, competitiveAdvantage: (pitch as any).competitiveAdvantage,
            fileUrl: pitch.fileUrl,
          });
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [id, submissionType]);

  if (loading || !submission) {
    return <div className="section-gap"><p className="text-muted">{loading ? 'Loading...' : 'Submission not found.'}</p></div>;
  }

  const isPitch = submission.type === 'pitch';

  const outcome = deriveOutcome(marks);
  const marksNum = marks === '' ? NaN : Number(marks);
  const marksInvalid = marks !== '' && (Number.isNaN(marksNum) || marksNum < 0 || marksNum > 100);
  const feedbackRequired = !!outcome?.requiresFeedback;
  const canSubmit = !!outcome && (!feedbackRequired || feedback.trim().length > 0);

  const handleSubmit = async () => {
    if (!outcome || !canSubmit) return;
    setProcessing(true);
    try {
      const scoreLine = `Score: ${marksNum}/100${outcome.distinction ? ' — Approved with Distinction' : ''}`;
      const feedbackWithScore = feedback.trim()
        ? `${scoreLine}\n\n${feedback.trim()}`
        : scoreLine;

      if (isPitch) {
        await pitchService.reviewPitch(submission.id, outcome.status, feedbackWithScore);
      } else {
        await pitchService.reviewIdea(submission.id, outcome.status, feedbackWithScore);
      }
      navigate('/app/dashboard/mentor');
    } catch (e) { console.error(e); }
    finally { setProcessing(false); }
  };

  return (
    <div className="section-gap">
      <div className="flex items-center gap-4">
        <Tooltip content="Return to dashboard" position="right">
          <button onClick={() => navigate('/app/dashboard/mentor')} className="p-2 rounded-lg hover:bg-shell transition-colors">
            <svg className="w-5 h-5 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </Tooltip>
        <div>
          <h1 className="text-2xl font-semibold text-text">{submission.title}</h1>
          <p className="text-muted mt-1">
            {submission.startupName} &middot; {isPitch ? 'Pitch' : 'Idea'} &middot; Submitted{' '}
            {new Date(submission.submittedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left 2/3 — Submission details */}
        <div className="lg:col-span-2">
          <Card>
            <h2 className="text-lg font-semibold text-text mb-5">{isPitch ? 'Pitch Details' : 'Idea Details'}</h2>
            <div className="space-y-4">
              {isPitch ? (
                <>
                  <ReadOnlyField label="Problem" value={submission.problem} />
                  <ReadOnlyField label="Solution" value={submission.solution} />
                  <ReadOnlyField label="Market Size" value={submission.marketSize} />
                  <ReadOnlyField label="Business Model" value={submission.businessModel} />
                  <ReadOnlyField label="Traction" value={submission.traction} />
                  <ReadOnlyField label="Team" value={submission.team} />
                  <ReadOnlyField label="Financials" value={submission.financials} />
                  <ReadOnlyField label="Ask Amount" value={submission.askAmount} />
                  <ReadOnlyField label="Use of Funds" value={submission.useOfFunds} />
                  <ReadOnlyField label="Competitive Advantage" value={submission.competitiveAdvantage} />
                </>
              ) : (
                <>
                  <ReadOnlyField label="Problem Statement" value={submission.problemStatement} />
                  <ReadOnlyField label="Proposed Solution" value={submission.proposedSolution} />
                  <ReadOnlyField label="Target Market" value={submission.targetMarket} />
                  <ReadOnlyField label="Unique Value Proposition" value={submission.uniqueValueProposition} />
                </>
              )}
            </div>
            {submission.fileUrl && (
              <div className="mt-5 pt-5 border-t border-line">
                <h3 className="text-sm font-medium text-muted mb-2">Attached Pitch Deck</h3>
                <a href={submission.fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-chart text-sm hover:underline">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  View Pitch Deck
                </a>
              </div>
            )}
          </Card>
        </div>

        {/* Right 1/3 — Score + Feedback + Submit (sticky on lg+) */}
        <aside className="lg:col-span-1 space-y-6 lg:sticky lg:top-6 lg:self-start">
          <Card>
            <div className="flex items-center justify-between gap-3 mb-1">
              <h2 className="text-lg font-semibold text-text">Score this submission</h2>
              <Tooltip content="Review your score, then submit" position="left">
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={!canSubmit || processing || marksInvalid}
                  size="sm"
                >
                  Submit
                </Button>
              </Tooltip>
            </div>
            <p className="text-muted text-xs mb-4">
              Outcome is derived automatically from the score.
            </p>

            {/* Marks input */}
            <div className="mb-4">
              <label htmlFor="marks" className="block text-xs font-medium text-text mb-1.5">
                Score <span className="text-muted">(0 – 100)</span>
              </label>
              <div className="relative">
                <input
                  id="marks"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={marks}
                  onChange={(e) => setMarks(e.target.value)}
                  placeholder="—"
                  className={`w-full pl-4 pr-12 py-3 rounded-card border bg-shell text-text placeholder:text-muted/50 focus:outline-none focus:ring-2 focus:ring-chart/30 focus:border-chart transition-all text-2xl font-display tabular-nums ${
                    marksInvalid ? 'border-danger' : 'border-line'
                  }`}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted text-sm">/ 100</span>
              </div>
              {marksInvalid && <p className="text-danger text-xs mt-1">Score must be between 0 and 100.</p>}
            </div>

            {/* Derived outcome preview */}
            <div className={`rounded-card border p-4 ${outcome ? toneClasses[outcome.tone].pill : 'bg-surface text-muted border-line'}`}>
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider font-semibold opacity-75">Outcome</span>
                {outcome?.distinction && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-chart text-black tracking-wide uppercase">
                    ★ Distinction
                  </span>
                )}
              </div>
              <div className="font-display text-xl tracking-wide leading-tight">
                {outcome ? outcome.label : EMPTY_OUTCOME.label}
              </div>
              <p className="text-xs leading-relaxed opacity-90 mt-1.5">
                {outcome ? outcome.message : EMPTY_OUTCOME.message}
              </p>
              {outcome && (
                <div className="mt-3 h-1.5 w-full bg-shell/50 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${toneClasses[outcome.tone].bar} transition-all duration-300`}
                    style={{ width: `${Math.max(0, Math.min(100, marksNum))}%` }}
                  />
                </div>
              )}
            </div>

            {/* Grading rubric */}
            <div className="mt-4 bg-surface border border-line rounded-card p-4">
              <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-2">Grading Rubric</div>
              <ul className="space-y-1.5 text-xs">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-danger mt-1.5 flex-shrink-0" />
                  <span className="text-text"><span className="font-semibold">&lt; 40</span> <span className="text-muted">— Rejected</span></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-warning mt-1.5 flex-shrink-0" />
                  <span className="text-text"><span className="font-semibold">40 – 59</span> <span className="text-muted">— Needs revision (with feedback)</span></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-success mt-1.5 flex-shrink-0" />
                  <span className="text-text"><span className="font-semibold">60 – 79</span> <span className="text-muted">— Approved</span></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-chart mt-1.5 flex-shrink-0" />
                  <span className="text-text"><span className="font-semibold">80 – 100</span> <span className="text-muted">— Approved with distinction</span></span>
                </li>
              </ul>
            </div>
          </Card>

          <Card>
            <button
              type="button"
              onClick={() => setFeedbackOpen((v) => !v)}
              aria-expanded={feedbackOpen}
              className="w-full flex items-center justify-between gap-3 text-left focus:outline-none focus:ring-2 focus:ring-chart/30 rounded-md"
            >
              <div className="flex items-baseline gap-3 flex-wrap">
                <h2 className="text-lg font-semibold text-text">Feedback</h2>
                <span
                  className={`text-[10px] italic px-2 py-0.5 rounded-full border ${
                    feedbackRequired
                      ? 'bg-warning/15 text-warning border-warning/30 not-italic font-medium'
                      : 'text-muted border-line'
                  }`}
                >
                  {feedbackRequired ? 'Required' : 'Optional'}
                </span>
                {feedback.trim() && (
                  <span className="text-[10px] text-success italic">· {feedback.trim().length} chars</span>
                )}
              </div>
              <svg
                className={`w-4 h-4 text-muted flex-shrink-0 transition-transform ${feedbackOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {feedbackOpen && (
              <div className="mt-4 animate-fade-in">
                <Tooltip content="Your feedback will be shared with the founder along with the score" position="top">
                  <div>
                    <Textarea
                      name="feedback"
                      placeholder={
                        outcome?.status === 'rejected'
                          ? 'Explain why this submission was rejected…'
                          : outcome?.status === 'revision_needed'
                          ? 'Explain specifically what needs to be revised…'
                          : 'Share notes or encouragement for the founder…'
                      }
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      rows={6}
                    />
                  </div>
                </Tooltip>
              </div>
            )}
          </Card>

        </aside>
      </div>

      {/* ─── Confirmation modal ─── */}
      <Modal isOpen={confirmOpen} onClose={() => !processing && setConfirmOpen(false)} title="Confirm submission">
        <div className="space-y-5">
          <p className="text-text text-sm leading-relaxed">
            You've marked this {isPitch ? 'pitch' : 'idea'} <span className="font-semibold tabular-nums">{marksNum}/100</span>.
            {outcome && (
              <>
                {' '}The {isPitch ? 'pitch' : 'idea'} is considered{' '}
                <span className={`font-semibold ${
                  outcome.tone === 'danger' ? 'text-danger'
                  : outcome.tone === 'warning' ? 'text-warning'
                  : outcome.tone === 'success' ? 'text-success'
                  : 'text-chart'
                }`}>
                  {outcome.label}
                </span>{' '}
                as per the score.
              </>
            )}
          </p>
          {feedbackRequired && !feedback.trim() && (
            <p className="text-xs italic text-warning">
              Feedback is required for this outcome — go back and add feedback before confirming.
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={processing}
              className="w-full sm:w-auto px-5 py-2.5 rounded-card border border-line bg-shell text-text text-sm font-medium hover:bg-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                await handleSubmit();
                setConfirmOpen(false);
              }}
              disabled={processing || !canSubmit}
              className="w-full sm:w-auto px-5 py-2.5 rounded-card bg-chart text-black text-sm font-semibold hover:bg-chart/90 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {processing ? 'Submitting…' : 'Confirm'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PitchReview;
